import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  REMARK_TAGS,
  type AddRemarkRequest,
  type AddRemarksRequest,
  type ApplicationRemarks,
  type EditRemarkRequest,
  type RankingView,
  type RemarkSummary,
  type RemarkTag,
  type RemarkView,
  type RemarkVisibility,
} from '@nest-recruit/shared';
import { apiDelete, apiGet, apiPatch, apiPost, errorMessage, type ApiError } from '../../../lib/api-client';
import { useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';

// F23.13 (ADR 0049): remarks, with the instant feel (CLAUDE.md §5a). A remark shows in the thread and
// on its Ranking row at once; the server confirms; only when the refetch has landed does the pending
// state let go. A refusal puts everything back and says why. Removing one offers Undo.

/** Under `['applications']`, so an import, a re-rank or a hint refreshes it too (#12). */
export function remarksKey(applicationId: string): readonly unknown[] {
  return ['applications', 'remarks', applicationId];
}

function rankingKeyOf(jobId: string): readonly unknown[] {
  return ['applications', 'ranking', jobId];
}

export function useApplicationRemarks(applicationId: string | null): UseQueryResult<ApplicationRemarks, ApiError> {
  return useQuery<ApplicationRemarks, ApiError>({
    queryKey: remarksKey(applicationId ?? ''),
    queryFn: () => apiGet<ApplicationRemarks>(`/applications/${applicationId ?? ''}/remarks`),
    enabled: applicationId !== null,
    placeholderData: (previous) => (previous?.applicationId === applicationId ? previous : undefined),
  });
}

export interface RemarkDraft {
  readonly tags: readonly RemarkTag[];
  readonly body: string;
  readonly visibility: RemarkVisibility;
  readonly mentionUserIds: readonly string[];
}

export interface RemarkActions {
  readonly add: (applicationId: string, draft: RemarkDraft) => Promise<boolean>;
  readonly addMany: (applicationIds: readonly string[], draft: Omit<RemarkDraft, 'mentionUserIds'>) => Promise<boolean>;
  readonly edit: (remark: RemarkView, draft: RemarkDraft) => Promise<boolean>;
  readonly remove: (remark: RemarkView) => Promise<void>;
  /** Applications with a remark on its way to the server: their row marks itself, nothing else flinches (§5a rule 4). */
  readonly pending: ReadonlySet<string>;
}

interface Snapshot {
  readonly ranking: RankingView | undefined;
  readonly remarks: ReadonlyMap<string, ApplicationRemarks | undefined>;
}

export function useRemarkActions(jobId: string): RemarkActions {
  const queryClient = useQueryClient();
  const session = useCurrentSession();
  const show = useToasts((toasts) => toasts.show);
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const mark = useCallback((ids: readonly string[], isOn: boolean): void => {
    setPending((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (isOn) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  /** Shown at once, then confirmed; the pending mark stays until the refetch lands (§5a rule 3). */
  const run = useCallback(
    async (applicationIds: readonly string[], change: (snapshot: Snapshot) => void, send: () => Promise<unknown>, failure: string): Promise<boolean> => {
      const snapshot = await takeSnapshot(queryClient, jobId, applicationIds);
      change(snapshot);
      mark(applicationIds, true);
      try {
        await send();
        await Promise.all([queryClient.invalidateQueries({ queryKey: rankingKeyOf(jobId) }), ...applicationIds.map((id) => queryClient.invalidateQueries({ queryKey: remarksKey(id) }))]);
        return true;
      } catch (error) {
        restore(queryClient, jobId, snapshot);
        show({ tone: 'danger', message: failure, detail: errorMessage(error) });
        return false;
      } finally {
        mark(applicationIds, false);
      }
    },
    [queryClient, jobId, mark, show],
  );

  const add = useCallback(
    (applicationId: string, draft: RemarkDraft): Promise<boolean> => {
      const optimistic = draftView(applicationId, draft, session?.id ?? null, session?.fullName ?? null);
      const body: AddRemarkRequest = { tags: [...draft.tags], body: draft.body, visibility: draft.visibility, mentionUserIds: draft.mentionUserIds.length > 0 && draft.visibility === 'team' ? [...draft.mentionUserIds] : [] };
      return run(
        [applicationId],
        () => putRemark(queryClient, jobId, optimistic),
        () => apiPost<RemarkView>(`/applications/${applicationId}/remarks`, body),
        "The remark wasn't saved",
      );
    },
    [run, queryClient, jobId, session],
  );

  const addMany = useCallback(
    (applicationIds: readonly string[], draft: Omit<RemarkDraft, 'mentionUserIds'>): Promise<boolean> => {
      const body: AddRemarksRequest = { applicationIds: [...applicationIds], tags: [...draft.tags], body: draft.body, visibility: draft.visibility };
      return run(
        applicationIds,
        () => {
          for (const applicationId of applicationIds) putRemark(queryClient, jobId, draftView(applicationId, { ...draft, mentionUserIds: [] }, session?.id ?? null, session?.fullName ?? null));
        },
        () => apiPost<{ readonly added: number }>('/remarks', body),
        "The remarks weren't saved",
      );
    },
    [run, queryClient, jobId, session],
  );

  const edit = useCallback(
    (remark: RemarkView, draft: RemarkDraft): Promise<boolean> => {
      const body: EditRemarkRequest = { tags: [...draft.tags], body: draft.body, visibility: draft.visibility, mentionUserIds: draft.visibility === 'team' ? [...draft.mentionUserIds] : [], version: remark.version };
      const changed: RemarkView = { ...remark, tags: draft.tags, body: draft.body.trim(), visibility: draft.visibility, isEdited: true };
      return run(
        [remark.applicationId],
        () => replaceRemark(queryClient, jobId, changed),
        () => apiPatch<RemarkView>(`/remarks/${remark.id}`, body),
        "The remark wasn't changed",
      );
    },
    [run, queryClient, jobId],
  );

  const remove = useCallback(
    async (remark: RemarkView): Promise<void> => {
      const isRemoved = await run(
        [remark.applicationId],
        () => dropRemark(queryClient, jobId, remark),
        () => apiDelete<void>(`/remarks/${remark.id}`),
        "The remark wasn't removed",
      );
      if (!isRemoved) return;
      // Undo beats confirm (§5a rule 8): it comes back as a fresh remark, without pinging anyone again.
      showUndo('Remark removed', () => {
        void add(remark.applicationId, { tags: remark.tags, body: remark.body, visibility: remark.visibility, mentionUserIds: [] });
      });
    },
    [run, queryClient, jobId, showUndo, add],
  );

  return { add, addMany, edit, remove, pending };
}

// --- The caches, changed by hand ------------------------------------------------------------------

async function takeSnapshot(queryClient: QueryClient, jobId: string, applicationIds: readonly string[]): Promise<Snapshot> {
  await queryClient.cancelQueries({ queryKey: rankingKeyOf(jobId) });
  await Promise.all(applicationIds.map((id) => queryClient.cancelQueries({ queryKey: remarksKey(id) })));
  return {
    ranking: queryClient.getQueryData<RankingView>(rankingKeyOf(jobId)),
    remarks: new Map(applicationIds.map((id) => [id, queryClient.getQueryData<ApplicationRemarks>(remarksKey(id))])),
  };
}

function restore(queryClient: QueryClient, jobId: string, snapshot: Snapshot): void {
  if (snapshot.ranking) queryClient.setQueryData(rankingKeyOf(jobId), snapshot.ranking);
  for (const [id, remarks] of snapshot.remarks) if (remarks) queryClient.setQueryData(remarksKey(id), remarks);
}

let draftCounter = 0;

function draftView(applicationId: string, draft: RemarkDraft, userId: string | null, name: string | null): RemarkView {
  draftCounter += 1;
  return {
    id: `pending-${draftCounter}`,
    applicationId,
    tags: REMARK_TAGS.filter((tag) => draft.tags.includes(tag)),
    body: draft.body.trim(),
    visibility: draft.visibility,
    authorUserId: userId,
    authorName: name,
    isMine: true,
    mentions: [],
    at: new Date().toISOString(),
    isEdited: false,
    version: 1,
  };
}

function updateThread(queryClient: QueryClient, applicationId: string, change: (remarks: readonly RemarkView[]) => readonly RemarkView[]): readonly RemarkView[] | null {
  const current = queryClient.getQueryData<ApplicationRemarks>(remarksKey(applicationId));
  if (!current) return null;
  const remarks = change(current.remarks);
  queryClient.setQueryData<ApplicationRemarks>(remarksKey(applicationId), { ...current, remarks });
  return remarks;
}

function updateRow(queryClient: QueryClient, jobId: string, applicationId: string, change: (summary: RemarkSummary) => RemarkSummary): void {
  const view = queryClient.getQueryData<RankingView>(rankingKeyOf(jobId));
  if (!view) return;
  queryClient.setQueryData<RankingView>(rankingKeyOf(jobId), {
    ...view,
    rows: view.rows.map((row) => (row.applicationId === applicationId ? { ...row, remarks: change(row.remarks) } : row)),
  });
}

/** A row's summary from a whole thread, newest first — the same reading the server does. */
export function summaryOf(remarks: readonly RemarkView[]): RemarkSummary {
  const latest = remarks[0];
  return {
    count: remarks.length,
    latest: latest ? { tags: latest.tags, body: latest.body, authorName: latest.authorName, at: latest.at, visibility: latest.visibility } : null,
    tags: REMARK_TAGS.filter((tag) => remarks.some((remark) => remark.tags.includes(tag))),
  };
}

function putRemark(queryClient: QueryClient, jobId: string, remark: RemarkView): void {
  const thread = updateThread(queryClient, remark.applicationId, (remarks) => [remark, ...remarks]);
  updateRow(queryClient, jobId, remark.applicationId, (summary) =>
    thread
      ? summaryOf(thread)
      : {
          count: summary.count + 1,
          latest: { tags: remark.tags, body: remark.body, authorName: remark.authorName, at: remark.at, visibility: remark.visibility },
          tags: REMARK_TAGS.filter((tag) => summary.tags.includes(tag) || remark.tags.includes(tag)),
        },
  );
}

function replaceRemark(queryClient: QueryClient, jobId: string, remark: RemarkView): void {
  const thread = updateThread(queryClient, remark.applicationId, (remarks) => remarks.map((item) => (item.id === remark.id ? remark : item)));
  if (thread) updateRow(queryClient, jobId, remark.applicationId, () => summaryOf(thread));
}

function dropRemark(queryClient: QueryClient, jobId: string, remark: RemarkView): void {
  const thread = updateThread(queryClient, remark.applicationId, (remarks) => remarks.filter((item) => item.id !== remark.id));
  updateRow(queryClient, jobId, remark.applicationId, (summary) => (thread ? summaryOf(thread) : { ...summary, count: Math.max(0, summary.count - 1) }));
}
